/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React from "react";
import { Dropdown, ButtonGroup } from "react-bootstrap";
import Button from "./Button";
import { useTheme } from "../theme/ThemeContext";
import "../styles/TableActionsToolbar.css";

/**
 * TableActionsToolbar
 * -------------------
 * A unified, responsive toolbar for table views.
 * 
 * Features:
 * - Search Bar with mutually exclusive Search/Clear icons.
 * - Focus highlighting for search input.
 * - Filter toggle and Reset buttons.
 * - Export dropdown (CSV, Excel, Print/PDF).
 * - Create button (optional).
 * 
 * Ends up responsive:
 * - Large: Full buttons
 * - Medium: Smaller buttons
 * - Mobile: Icon-only buttons with hover titles
 */
export default function TableActionsToolbar({
    searchInput,
    onSearchChange,
    onSearchSubmit,
    onClearSearch,
    isSearchActive,
    onOpenFilters,
    onResetFilters,
    hasFilters,
    onExport,
    onCreate,
    onHistory,
    showCreate = true,
    showHistory = true,
    isStackedView,
    onToggleStackedView
}) {
    const { theme } = useTheme();

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            onSearchSubmit();
        }
    };

    return (
        <div className="table-actions-toolbar">
            {/* Search Bar */}
            <div className="search-container">
                <div style={{ position: "relative", width: "100%" }}>
                    {isSearchActive ? (
                        <i
                            className="bi bi-x-circle-fill"
                            onClick={onClearSearch}
                            style={{
                                position: "absolute",
                                right: 12,
                                top: "50%",
                                transform: "translateY(-50%)",
                                color: "#dc3545", // Red for clear
                                cursor: "pointer",
                                fontSize: "16px",
                                zIndex: 10
                            }}
                            title="Clear Search"
                        ></i>
                    ) : (
                        <i
                            className="bi bi-search"
                            onClick={onSearchSubmit}
                            style={{
                                position: "absolute",
                                right: 12,
                                top: "50%",
                                transform: "translateY(-50%)",
                                color: theme.primary,
                                cursor: "pointer",
                                fontSize: "16px",
                                zIndex: 10
                            }}
                            title="Search"
                        ></i>
                    )}

                    <input
                        type="text"
                        placeholder="Search records..."
                        value={searchInput}
                        onChange={onSearchChange}
                        onKeyDown={handleKeyDown}
                        style={{
                            width: "100%",
                            padding: "10px 40px 10px 12px", // Right padding for icon
                            fontSize: "15px",
                            borderRadius: theme.buttonRadius,
                            border: "1px solid #d1d5db",
                            backgroundColor: "#fff",
                            transition: "all 0.2s",
                            outline: "none"
                        }}
                        onFocus={(e) => e.target.style.borderColor = theme.primary}
                        onBlur={(e) => e.target.style.borderColor = "#d1d5db"}
                    />
                </div>
            </div>

            {/* Actions Group */}
            <div className="actions-group">
                {onToggleStackedView && (
                    <div
                        className="d-none d-lg-flex"
                        style={{
                            background: "#f3f4f6",
                            padding: 4,
                            borderRadius: theme.buttonRadius,
                            gap: 4,
                            marginRight: 8,
                            alignSelf: "center"
                        }}
                    >
                        <div
                            onClick={() => isStackedView && onToggleStackedView()}
                            style={{
                                cursor: "pointer",
                                padding: "6px 10px",
                                borderRadius: theme.buttonRadius - 2,
                                background: !isStackedView ? "#fff" : "transparent",
                                boxShadow: !isStackedView ? "0 1px 2px rgba(0,0,0,0.1)" : "none",
                                color: !isStackedView ? theme.primary : "#6b7280",
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                transition: "all 0.2s"
                            }}
                            title="Table View"
                        >
                            <i className="bi bi-table" style={{ fontSize: 14 }}></i>
                        </div>
                        <div
                            onClick={() => !isStackedView && onToggleStackedView()}
                            style={{
                                cursor: "pointer",
                                padding: "6px 10px",
                                borderRadius: theme.buttonRadius - 2,
                                background: isStackedView ? "#fff" : "transparent",
                                boxShadow: isStackedView ? "0 1px 2px rgba(0,0,0,0.1)" : "none",
                                color: isStackedView ? theme.primary : "#6b7280",
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                transition: "all 0.2s"
                            }}
                            title="Card View"
                        >
                            <i className="bi bi-card-list" style={{ fontSize: 14 }}></i>
                        </div>
                    </div>
                )}

                <Button
                    mode="bordered"
                    onClick={onOpenFilters}
                    aria-label="Filters"
                    className="toolbar-btn"
                    title="Filters"
                >
                    <i className="bi bi-funnel" />
                    <span className="toolbar-btn-label">Filters</span>
                </Button>

                {hasFilters && (
                    <Button
                        mode="outlined"
                        onClick={onResetFilters}
                        className="toolbar-btn"
                        title="Reset Filters"
                    >
                        <i className="bi bi-x" />
                        <span className="toolbar-btn-label">Reset</span>
                    </Button>
                )}

                <Dropdown as={ButtonGroup}>
                    <Dropdown.Toggle
                        variant="light"
                        id="dropdown-export"
                        className="toolbar-btn"
                        title="Export"
                        style={{
                            backgroundColor: "#fff",
                            color: "#4b5563",
                            border: "1px solid #d1d5db",
                            borderRadius: theme.buttonRadius,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
                        }}
                    >
                        <i className="bi bi-download" />
                        <span className="toolbar-btn-label">Export</span>
                    </Dropdown.Toggle>

                    <Dropdown.Menu style={{ border: "none", boxShadow: "0 10px 25px rgba(0,0,0,0.1)", borderRadius: 12, padding: 8, minWidth: 160 }}>
                        <Dropdown.Header>Format</Dropdown.Header>
                        <Dropdown.Item onClick={() => onExport('csv')} style={{ borderRadius: 6, padding: "8px 12px", fontSize: 14 }}>
                            <i className="bi bi-filetype-csv me-2 text-success"></i> CSV
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => onExport('xlsx')} style={{ borderRadius: 6, padding: "8px 12px", fontSize: 14 }}>
                            <i className="bi bi-file-earmark-spreadsheet me-2 text-success"></i> Excel
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item onClick={() => onExport('pdf')} style={{ borderRadius: 6, padding: "8px 12px", fontSize: 14 }}>
                            <i className="bi bi-printer me-2 text-secondary"></i> Print / PDF
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>

                {showHistory && (
                    <Button
                        mode="bordered"
                        onClick={onHistory}
                        aria-label="History"
                        className="toolbar-btn"
                        title="History"
                    >
                        <i className="bi bi-clock-history" />
                        <span className="toolbar-btn-label">History</span>
                    </Button>
                )}



                {showCreate && (
                    <Button
                        mode="primary-bold"
                        onClick={onCreate}
                        className="toolbar-btn"
                        title="Create New"
                    >
                        <i className="bi bi-plus-lg" />
                        <span className="toolbar-btn-label">Create</span>
                    </Button>
                )}
            </div>
        </div>
    );
}
